import { FunctionComponent, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import JunoPage from "../../components/JunoPage/JunoPage";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { SearchableTable } from "@fyrstain/hl7-front-library";
import i18n from "i18next";
import { HealthcareService } from "fhir/r5";
import {
  getOfficialIdentifierValue,
  getReferenceValue,
} from "../../utils/fhirReferences";
import "./HealthcareServices.css";
import { useErrorNavigation } from "../../services/useErrorNavigation";

const HealthcareServices: FunctionComponent = () => {
  //////////////////////////////
  //        Navigation        //
  //////////////////////////////

  const navigate = useNavigate();

  //////////////////////////////
  //    Search config         //
  //////////////////////////////

  const onDetails = useCallback(
    (id: string) => {
      navigate("/HealthcareService/" + id);
    },
    [navigate]
  );

  const onError = useErrorNavigation();

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <JunoPage titleKey={i18n.t("title.healthcareservicelist")} needsLogin={true}>
      <div className="healthcare-services-table">
        <SearchableTable
          searchCriteriaProperties={{
            title: i18n.t("title.searchcriteria"),
            submitButtonLabel: i18n.t("button.search"),
            resetButtonLabel: i18n.t("button.reset"),
            language: i18n.t,
            fixedParameters: {
              _elements: "id,name,identifier,providedBy,location,active",
              _sort: "name",
              _tag: "internal",
            },
            inputs: [
              {
                label: i18n.t("label.healthcareservicename"),
                type: "text",
                searchParamsName: "name:contains",
              },
              {
                label: i18n.t("label.identifier"),
                type: "text",
                searchParamsName: "identifier",
              },
              {
                label: i18n.t("label.providedby"),
                type: "text",
                searchParamsName: "organization.identifier",
              },
              {
                label: i18n.t("label.location"),
                type: "text",
                searchParamsName: "location.identifier",
              },
            ],
          }}
          paginatedTableProperties={{
            columns: [
              {
                header: i18n.t("label.healthcareservicename"),
                dataField: "name",
                width: "35%",
              },
              {
                header: i18n.t("label.identifier"),
                dataField: "identifier",
                width: "20%",
              },
              {
                header: i18n.t("label.providedby"),
                dataField: "providedBy",
                width: "25%",
              },
              {
                header: i18n.t("label.locations"),
                dataField: "locationCount",
                width: "20%",
              },
            ],
            action: [
              {
                icon: faEye,
                onClick: onDetails,
              },
            ],
            mapResourceToData: (resource: HealthcareService) => {
              const primaryIdentifier = getOfficialIdentifierValue(resource.identifier);

              return {
                id: resource.id ?? "N/A",
                name: resource.name ?? "N/A",
                identifier: primaryIdentifier || resource.id || "N/A",
                providedBy: getReferenceValue(resource.providedBy) || "N/A",
                locationCount: resource.location?.length
                  ? `${resource.location.length}`
                  : "N/A",
              };
            },
            searchProperties: {
              serverUrl: process.env.REACT_APP_FHIR_URL ?? "fhir",
              resourceType: "HealthcareService",
            },
            onError: onError,
          }}
        />
      </div>
    </JunoPage>
  );
};

export default HealthcareServices;
